import { DialogContent, Stack } from "@mui/material";
import { Text, Title } from "@src/appV2/redesign/system/ui-components";
import { usePostBatchReactions } from "@src/appV2/redesign/Workplace/Review/api/usePostBatchReactions";
import { type WorkplaceCommentType } from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { PeerReviewSwipeDeck } from "@src/appV2/redesign/Workplace/Review/PeerReview/PeerReviewSwipeDeck";
import { type Reaction } from "@src/appV2/redesign/Workplace/Review/types";
import pluralize from "pluralize";
import { useCallback } from "react";

import { type StepAction } from "../types";

interface PeerReviewsStepProps {
  workplaceId: string;
  workplaceName: string;
  comments: WorkplaceCommentType[];
  onAction: (action: StepAction) => void;
}

export function PeerReviewsStep(props: PeerReviewsStepProps) {
  const { workplaceId, workplaceName, comments, onAction } = props;

  const { mutateAsync: postBatchReactions } = usePostBatchReactions({ workplaceId });

  const submitReactionsAndAdvance = useCallback(
    async (
      reactions: readonly { commentId: string; reaction: Reaction }[],
      actionType: "NEXT_STEP" | "SKIP_STEP"
    ) => {
      if (reactions.length > 0) {
        try {
          await postBatchReactions({ reactions: [...reactions] });
        } catch {
          // logging and error message is handled in the usePostBatchReactions hook
        }
      }

      onAction({ type: actionType });
    },
    [postBatchReactions, onAction]
  );

  const handleComplete = useCallback(
    async (reactions: readonly { commentId: string; reaction: Reaction }[]) => {
      await submitReactionsAndAdvance(reactions, "NEXT_STEP");
    },
    [submitReactionsAndAdvance]
  );

  const handleSkip = useCallback(
    async (reactions: readonly { commentId: string; reaction: Reaction }[]) => {
      await submitReactionsAndAdvance(reactions, "SKIP_STEP");
    },
    [submitReactionsAndAdvance]
  );

  return (
    <DialogContent sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <Stack spacing={3} sx={{ mb: 5 }}>
        <Text
          variant="caption"
          color={(theme) => theme.palette.text.secondary}
          sx={{ textTransform: "uppercase", letterSpacing: 1 }}
        >
          {workplaceName}
        </Text>
        <Title variant="h4" component="h2">
          Do these match your experience?
        </Title>
        <Text variant="body2">
          Swipe right to like, left to dislike. Just {pluralize("review", comments.length, true)}.
        </Text>
      </Stack>
      <PeerReviewSwipeDeck
        comments={comments}
        workplaceId={workplaceId}
        onComplete={handleComplete}
        onSkip={handleSkip}
      />
    </DialogContent>
  );
}
